import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function MyLeads() {
  const { api, navigate } = useAuth();
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [savingMeeting, setSavingMeeting] = useState(false);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      const data = await api('/advisors/leads');
      setLeads(data.leads);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Tell the system this lead turned into a booked meeting. This is what
  // eventually teaches Google Ads which clicks are worth paying for, so it
  // matters that it is easy to press and easy to undo.
  const toggleMeeting = async (lead) => {
    const booked = !lead.meeting_booked;
    setSavingMeeting(true);
    try {
      const data = await api(`/advisors/leads/${lead.id}/meeting`, {
        method: 'POST',
        body: JSON.stringify({ booked })
      });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? data.lead : l)));
      setSelectedLead(data.lead);
    } catch (error) {
      console.error('Failed to update meeting status:', error);
      alert('Sorry, that did not save. Please try again.');
    } finally {
      setSavingMeeting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-navy-900 mb-2">My Leads</h1>
          <p className="text-navy-600">All leads assigned to you through the program</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-serif text-navy-900">{leads.length}</p>
          <p className="text-sm text-navy-500">total leads</p>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="card-elevated text-center py-16">
          <div className="w-20 h-20 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-navy-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h2 className="font-serif text-2xl text-navy-900 mb-3">No leads yet</h2>
          <p className="text-navy-600 mb-6 max-w-md mx-auto">
            Once you purchase a lead package and leads are assigned to you, they'll appear here.
          </p>
          <button onClick={() => navigate('/purchase')} className="btn-primary">
            Purchase Leads
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Leads List */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-cream-200">
                      <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Lead</th>
                      <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Contact</th>
                      <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Received</th>
                      <th className="text-right text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr 
                        key={lead.id} 
                        className={`border-b border-cream-100 hover:bg-cream-50 cursor-pointer transition-colors ${
                          selectedLead?.id === lead.id ? 'bg-gold-50' : ''
                        }`}
                        onClick={() => setSelectedLead(lead)}
                      >
                        <td className="py-4 px-4">
                          <p className="font-medium text-navy-900 flex items-center gap-2">
                            {lead.name}
                            {lead.meeting_booked ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-highlight-700 bg-highlight-50 border border-highlight-200 rounded-full px-2 py-0.5" title="You marked this as a booked meeting">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                                Meeting
                              </span>
                            ) : null}
                          </p>
                          <p className="text-sm text-navy-500 truncate max-w-[200px]">{lead.message}</p>
                        </td>
                        <td className="py-4 px-4">
                          <a 
                            href={`mailto:${lead.email}`} 
                            className="text-gold-600 hover:text-gold-700 text-sm block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {lead.email}
                          </a>
                          {lead.phone && (
                            <a 
                              href={`tel:${lead.phone}`} 
                              className="text-navy-500 hover:text-navy-700 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {lead.phone}
                            </a>
                          )}
                        </td>
                        <td className="py-4 px-4 text-navy-500 text-sm">
                          {new Date(lead.assigned_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <svg className="w-5 h-5 text-navy-400 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Lead Detail Sidebar */}
          <div className="lg:col-span-1">
            {selectedLead ? (
              <div className="card-elevated sticky top-24">
                <div className="mb-6">
                  <h2 className="font-serif text-xl text-navy-900 mb-1">{selectedLead.name}</h2>
                  <p className="text-sm text-navy-500">
                    Received {new Date(selectedLead.assigned_at).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-xs text-navy-500 uppercase tracking-wide">Email</label>
                    <a 
                      href={`mailto:${selectedLead.email}`}
                      className="block text-gold-600 hover:text-gold-700 font-medium mt-1"
                    >
                      {selectedLead.email}
                    </a>
                  </div>
                  
                  {selectedLead.phone && (
                    <div>
                      <label className="text-xs text-navy-500 uppercase tracking-wide">Phone</label>
                      <a 
                        href={`tel:${selectedLead.phone}`}
                        className="block text-navy-900 hover:text-gold-600 font-medium mt-1"
                      >
                        {selectedLead.phone}
                      </a>
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-navy-500 uppercase tracking-wide">Message</label>
                    <p className="mt-1 text-navy-700 whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                </div>

                {/* Booked-meeting marker */}
                <div className="mb-4 pt-4 border-t border-cream-200">
                  {selectedLead.meeting_booked ? (
                    <div className="rounded-lg bg-highlight-50 border border-highlight-200 p-4">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-highlight-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div className="flex-1">
                          <p className="font-medium text-highlight-800">Meeting booked</p>
                          {selectedLead.meeting_booked_at && (
                            <p className="text-xs text-highlight-700 mt-0.5">
                              Marked {new Date(selectedLead.meeting_booked_at).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric'
                              })}
                            </p>
                          )}
                          <button
                            onClick={() => toggleMeeting(selectedLead)}
                            disabled={savingMeeting}
                            className="text-xs text-highlight-700 underline mt-2 hover:text-highlight-800 disabled:opacity-50"
                          >
                            {savingMeeting ? 'Saving...' : 'Undo'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleMeeting(selectedLead)}
                        disabled={savingMeeting}
                        className="btn-primary w-full text-center disabled:opacity-50"
                      >
                        {savingMeeting ? 'Saving...' : 'I booked a meeting with this lead'}
                      </button>
                      <p className="text-xs text-navy-500 mt-2 text-center">
                        Telling us helps us send you better leads.
                      </p>
                    </>
                  )}
                </div>

                <div className="flex gap-3">
                  <a 
                    href={`mailto:${selectedLead.email}?subject=Your Luxury Travel Inquiry`}
                    className="btn-primary flex-1 text-center"
                  >
                    Email
                  </a>
                  {selectedLead.phone && (
                    <a 
                      href={`tel:${selectedLead.phone}`}
                      className="btn-secondary flex-1 text-center"
                    >
                      Call
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="card bg-cream-50 text-center py-12">
                <svg className="w-12 h-12 text-navy-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <p className="text-navy-500">Select a lead to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
